import React, {useCallback, useRef} from 'react';
import type {NodeLocationInfo} from 'Contexts/NodeCallBackContext';
import {useAnimationFrame} from 'Hooks/useAnimationFrame';
import {AstView} from 'rendition-core';

interface Props {
    rootXY: {x: number; y: number};
    locationCallBack: () => NodeLocationInfo;
    view: AstView;
    scaleRef: React.MutableRefObject<number>;
}

interface VerticalProps extends Props {
    xPos: number;
}

const borderSizeToAccountFor = 0.5;

export const VerticalSnapGuide: React.FC<VerticalProps> = ({rootXY, locationCallBack, xPos, view, scaleRef}) => {
    const ref = useRef<null | HTMLDivElement>(null);

    const updateOnFrame = useCallback(() => {
        if (ref.current) {
            const location = locationCallBack();
            const absoluteXDiff = (xPos - view.tree.position.absoluteX) * scaleRef.current;

            ref.current.style.height = location.actual.height + 'px';
            ref.current.style.left = location.actual.x + absoluteXDiff - rootXY.x + 'px';
            ref.current.style.top = location.actual.y - rootXY.y - borderSizeToAccountFor + 'px';
        }
    }, [locationCallBack, rootXY.x, rootXY.y, scaleRef, view.tree.position.absoluteX, xPos]);

    useAnimationFrame(updateOnFrame);

    return <div ref={ref} className="w-px bg-red-500 absolute z-50" />;
};

interface HorizontalProps extends Props {
    yPos: number;
}

export const HorizontalSnapGuide: React.FC<HorizontalProps> = ({rootXY, locationCallBack, yPos, view, scaleRef}) => {
    const ref = useRef<null | HTMLDivElement>(null);

    const updateOnFrame = useCallback(() => {
        if (ref.current) {
            const location = locationCallBack();
            const absoluteYDiff = (yPos - view.tree.position.absoluteY) * scaleRef.current;

            ref.current.style.width = location.actual.width + 'px';
            ref.current.style.left = location.actual.x - rootXY.x - borderSizeToAccountFor + 'px';
            ref.current.style.top = location.actual.y + absoluteYDiff - rootXY.y + 'px';
        }
    }, [locationCallBack, rootXY.x, rootXY.y, scaleRef, view.tree.position.absoluteY, yPos]);

    useAnimationFrame(updateOnFrame);

    return <div ref={ref} className="h-px bg-red-500 absolute z-50" />;
};
